import React, { useState, useEffect } from "react";
import { Box, Checkbox, Collapse, FormControlLabel, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import { Info, ChevronDown, ChevronUp, Lock, Check, X } from "lucide-react";
import { CustomTextField } from "../ui/CustomTextField.tsx";

interface ProfileData {
  profileName: string;
  collections: string[];
  includeHistory: boolean;
  systemInstruction: string;
  modelId: string;
  numSearchDocuments: number | null;
  numChatMessages: number | null;
  temperature: number | null;
}

interface ProfileFormStepsProps {
  activeStep: number;
  showPreview: boolean;
  profileData: ProfileData;
  availableCollections: string[];
  isLoading: boolean;
  isDarkMode: boolean;
  adjustmentsExpanded: boolean;
  setAdjustmentsExpanded: (expanded: boolean) => void;
  instructionsExpanded: boolean;
  setInstructionsExpanded: (expanded: boolean) => void;
  handleCollectionToggle: (collection: string) => void;
  setProfileData: React.Dispatch<React.SetStateAction<ProfileData>>;
  isDefaultCollection: (collection: string) => boolean;
  getUniqueCollections: () => string[];
  isEditMode?: boolean;
  onNext?: () => void;
  canGoNext?: boolean;
  nextButtonText?: string;
}

const FormContainer = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  padding: "24px",
  backgroundColor: $isDark ? "#18181B" : "#F9FAFB",
  borderRadius: "12px",
  marginBottom: "16px",
  minHeight: "235px",
}));

const InfoBox = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  alignItems: "flex-start",
  padding: "8px",
  backgroundColor: $isDark ? "rgba(34, 197, 94, 0.1)" : "rgba(34, 197, 94, 0.05)",
  borderRadius: "8px",
  marginBottom: "24px",
  border: "1px solid #22C55E",
  "& .info-icon": {
    color: $isDark ? "#22C55E" : "#16A34A",
    marginRight: "8px",
  },
  "& .info-text": {
    color: $isDark ? "#D1FAE5" : "#065F46",
    fontSize: "13px",
    "@media (max-width: 600px)": {
      fontSize: "12px",
    },
  },
}));

const CollectionItem = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  alignItems: "center",
  padding: "8px",
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
  color: $isDark ? "#FFFFFF" : "#1F2937",
  borderRadius: "8px",
  marginBottom: "8px",
  "& .MuiFormControlLabel-root": {
    margin: 0,
    width: "100%",
  },
  "& .MuiCheckbox-root": {
    color: $isDark ? "#71717A" : "#9CA3AF",
    "&.Mui-checked": {
      color: "#22C55E",
    },
  },
}));

const DefaultCollectionTag = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "inline-block",
  padding: "4px 8px",
  backgroundColor: "#22C55E",
  color: "white",
  borderRadius: "4px",
  fontSize: "12px",
  marginLeft: "8px",
}));

const CollapseCard = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
  borderRadius: "8px",
  overflow: "hidden",
  marginBottom: "16px",
}));

const CollapseHeader = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 16px",
  cursor: "pointer",
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
  color: $isDark ? "#FFFFFF" : "#1F2937",
  fontWeight: "bold",
  borderBottom: `1px solid ${$isDark ? "#3F3F46" : "#E5E7EB"}`,
  "&:hover": {
    backgroundColor: $isDark ? "#3F3F46" : "#E5E7EB",
  },
  "@media (max-width: 600px)": {
    fontSize: "12px",
  },
}));

const CollapseContent = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  padding: "16px",
  backgroundColor: $isDark ? "#27272A" : "#F3F4F6",
}));

const StyledTextarea = styled("textarea")<{ $isDark?: boolean }>(({ $isDark }) => ({
  backgroundColor: $isDark ? "#27272A" : "#F9FAFB",
  color: $isDark ? "#FFFFFF" : "#1F2937",
  border: `1px solid ${$isDark ? "#3F3F46" : "#D1D5DB"}`,
  borderRadius: "8px",
  padding: "12px",
  fontFamily: "inherit",
  fontSize: "14px",
  resize: "none",
  lineHeight: "1.5",
  height: "160px",
  width: "100%",
  "&:focus": {
    outline: "none",
    borderColor: "#22C55E",
  },
  "&::placeholder": {
    color: $isDark ? "#71717A" : "#9CA3AF",
  },
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: $isDark ? "#27272A" : "#F3F4F6",
    borderRadius: "0 8px 8px 0",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: $isDark ? "#3F3F46" : "#D1D5DB",
    borderRadius: "4px",
  },
  "@media (max-width: 600px)": {
    fontSize: "12px",
  },
}));

const ReadOnlyField = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  position: "relative",
  padding: "12px 14px",
  backgroundColor: $isDark ? "#3F3F46" : "#E5E7EB",
  color: $isDark ? "#D4D4D8" : "#6B7280",
  borderRadius: "8px",
  border: `1px solid ${$isDark ? "#52525B" : "#D1D5DB"}`,
  fontSize: "14px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& .lock-icon": {
    color: $isDark ? "#71717A" : "#9CA3AF",
  },
  "@media (max-width: 600px)": {
    fontSize: "12px",
  },
}));

const ProfileFormSteps = ({
  activeStep,
  showPreview,
  profileData,
  availableCollections,
  isLoading,
  isDarkMode,
  adjustmentsExpanded,
  setAdjustmentsExpanded,
  instructionsExpanded,
  setInstructionsExpanded,
  handleCollectionToggle,
  setProfileData,
  isDefaultCollection,
  getUniqueCollections,
  isEditMode = false,
  onNext,
  canGoNext,
  nextButtonText,
}: ProfileFormStepsProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [textConfig, setTextConfig] = useState<any>({});
  const [modelIdOpen, setModelIdOpen] = useState(false);
  const [temperatureOpen, setTemperatureOpen] = useState(false);

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const renderPreview = () => (
    <FormContainer $isDark={isDarkMode}>
      <InfoBox $isDark={isDarkMode}>
        <Info className="info-icon" size={isMobile ? 28 : 34} />
        <Typography className="info-text" sx={{ fontSize: isMobile ? "12px" : "13px" }}>
          {textConfig.PROFILE_FORM_STEPS_PREVIEW_INFO}
        </Typography>
      </InfoBox>
      
      <Box sx={{ 
        backgroundColor: isDarkMode ? "#27272A" : "#F3F4F6", 
        borderRadius: "8px", 
        padding: "20px",
        color: isDarkMode ? "#FFFFFF" : "#1F2937",
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ 
            fontSize: isMobile ? "16px" : "18px", 
            fontWeight: "bold", 
            color: "#22C55E", 
            mb: 2 
          }}>
            {textConfig.PROFILE_FORM_STEPS_BASIC_INFO}
          </Typography>
          <Box sx={{ 
            backgroundColor: "#18181B", 
            padding: "16px", 
            borderRadius: "8px" 
          }}>
            <Typography 
              sx={{ 
                fontWeight: "bold", 
                fontSize: isMobile ? "12px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={profileData.profileName}
            >
              {profileData.profileName}
            </Typography>
            <Typography 
              sx={{ 
                fontSize: isMobile ? "12px" : "12px", 
                color: isDarkMode ? "#A1A1AA" : "#6B7280",
                mt: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={textConfig.PROFILE_FORM_STEPS_PROFILE_ID?.replace('{id}', profileData.profileName.toLowerCase().replace(/[^a-z0-9_-]/g, "_"))}
            >
              {textConfig.PROFILE_FORM_STEPS_PROFILE_ID?.replace('{id}', profileData.profileName.toLowerCase().replace(/[^a-z0-9_-]/g, "_"))}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ 
            fontSize: isMobile ? "16px" : "18px", 
            fontWeight: "bold", 
            color: "#22C55E", 
            mb: 2 
          }}>
            {textConfig.PROFILE_FORM_STEPS_COLLECTIONS_COUNT?.replace('{count}', getUniqueCollections().length.toString())}
          </Typography>
          <Box sx={{ 
            backgroundColor: "#18181B", 
            padding: "16px", 
            borderRadius: "8px",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}>
            {getUniqueCollections().map((collection) => (
              <Box 
                key={collection} 
                sx={{ 
                  backgroundColor: isDarkMode ? "#3F3F46" : "#E5E7EB", 
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: isMobile ? "12px" : "14px",
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={collection}
              >
                {collection}
              </Box>
            ))}
          </Box>
        </Box>
        
        <Box>
          <Typography sx={{ 
            fontSize: isMobile ? "16px" : "18px", 
            fontWeight: "bold", 
            color: "#22C55E", 
            mb: 2 
          }}>
            {textConfig.PROFILE_FORM_STEPS_PROMPT_CONFIG}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ mb: 2, fontSize: isMobile ? "12px" : "14px" }}>
              {textConfig.PROFILE_FORM_STEPS_ACTIVATED_FEATURES}:
            </Typography>
            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ 
                color: "#22C55E", 
                display: "inline-flex", 
                alignItems: "center"
              }}>
                ✓
              </Box>
              <Typography sx={{ 
                fontSize: isMobile ? "10px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {textConfig.PROFILE_FORM_STEPS_DOCUMENT_CONTEXT}
              </Typography>
            </Box>
            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ 
                color: profileData.includeHistory ? "#22C55E" : "#FF4444", 
                display: "inline-flex", 
                alignItems: "center"
              }}>
                {profileData.includeHistory ? "✓" : "✗"}
              </Box>
              <Typography sx={{ 
                fontSize: isMobile ? "10px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {textConfig.PROFILE_FORM_STEPS_CONVERSATION_HISTORY}
              </Typography>
            </Box>
            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ 
                color: profileData.systemInstruction.trim() ? "#22C55E" : "#FF4444", 
                display: "inline-flex", 
                alignItems: "center"
              }}>
                {profileData.systemInstruction.trim() ? "✓" : "✗"}
              </Box>
              <Typography sx={{ 
                fontSize: isMobile ? "10px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {textConfig.PROFILE_FORM_STEPS_CUSTOM_INSTRUCTIONS}
              </Typography>
            </Box>

            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ 
                color: profileData.modelId && profileData.modelId.trim() ? "#22C55E" : "#FF4444", 
                display: "inline-flex", 
                alignItems: "center"
              }}>
                {profileData.modelId && profileData.modelId.trim() ? "✓" : "✗"}
              </Box>
              <Typography sx={{ 
                fontSize: isMobile ? "10px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                Modelo de IA: {profileData.modelId && profileData.modelId.trim() ? profileData.modelId : "No seleccionado"}
              </Typography>
            </Box>

            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ 
                color: profileData.temperature !== null ? "#22C55E" : "#FF4444", 
                display: "inline-flex", 
                alignItems: "center"
              }}>
                {profileData.temperature !== null ? "✓" : "✗"}
              </Box>
              <Typography sx={{ 
                fontSize: isMobile ? "10px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                Temperatura: {profileData.temperature !== null ? profileData.temperature : "No configurado"}
              </Typography>
            </Box>

            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ 
                color: profileData.numSearchDocuments !== null ? "#22C55E" : "#FF4444", 
                display: "inline-flex", 
                alignItems: "center"
              }}>
                {profileData.numSearchDocuments !== null ? "✓" : "✗"}
              </Box>
              <Typography sx={{ 
                fontSize: isMobile ? "10px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                Número de documentos de búsqueda: {profileData.numSearchDocuments !== null ? profileData.numSearchDocuments : "No configurado"}
              </Typography>
            </Box>

            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ 
                color: profileData.numChatMessages !== null ? "#22C55E" : "#FF4444", 
                display: "inline-flex", 
                alignItems: "center"
              }}>
                {profileData.numChatMessages !== null ? "✓" : "✗"}
              </Box>
              <Typography sx={{ 
                fontSize: isMobile ? "10px" : "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                Número de mensajes de chat: {profileData.numChatMessages !== null ? profileData.numChatMessages : "No configurado"}
              </Typography>
            </Box>
          </Box>



          {profileData.systemInstruction.trim() && (
            <Box>
              <Typography sx={{ 
                mb: 1, 
                fontSize: isMobile ? "12px" : "14px" 
              }}>
                {textConfig.PROFILE_FORM_STEPS_CUSTOM_INSTRUCTIONS_LABEL}:
              </Typography>
              <Box sx={{ 
                backgroundColor: "#18181B", 
                padding: "16px", 
                borderRadius: "8px" 
              }}>
                <Typography sx={{ 
                  fontSize: isMobile ? "10px" : "14px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>
                  {profileData.systemInstruction}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </FormContainer>
  );

  const renderStep0 = () => (
    <FormContainer $isDark={isDarkMode}>
      <InfoBox $isDark={isDarkMode}>
        <Info className="info-icon" size={isMobile ? 28 : 34} />
        <Typography className="info-text" sx={{ fontSize: isMobile ? "12px" : "13px" }}>
          {isEditMode ? 
            textConfig.PROFILE_FORM_STEPS_EDIT_MODE_INFO :
            textConfig.PROFILE_FORM_STEPS_CREATE_MODE_INFO
          }
        </Typography>
      </InfoBox>
      
      <Typography 
        sx={{ 
          mb: 2,
          color: isDarkMode ? "#FFFFFF" : "#1F2937",
          fontSize: isMobile ? "14px" : "14px"
        }}
      >
        {textConfig.PROFILE_FORM_STEPS_PROFILE_NAME_LABEL}
      </Typography>
      
      {isEditMode ? (
        <ReadOnlyField $isDark={isDarkMode}>
          <span>{profileData.profileName}</span>
          <Lock className="lock-icon" size={16} />
        </ReadOnlyField>
      ) : (
        <CustomTextField
          value={profileData.profileName}
          onChange={(e) => setProfileData(prev => ({ ...prev, profileName: e.target.value }))}
          placeholder={textConfig.PROFILE_FORM_STEPS_PROFILE_NAME_PLACEHOLDER}
          isFullWidth
        />
      )}
    </FormContainer>
  );

  const renderStep1 = () => (
    <FormContainer $isDark={isDarkMode}>
      <InfoBox $isDark={isDarkMode}>
        <Info className="info-icon" size={isMobile ? 28 : 34} />
        <Typography className="info-text" sx={{ fontSize: isMobile ? "12px" : "13px" }}>
          {textConfig.PROFILE_FORM_STEPS_COLLECTIONS_INFO}
        </Typography>
      </InfoBox>
      
      <Typography 
        sx={{ 
          mb: 2,
          color: isMobile ? "#1BCF66" : "#1BCF66",
          fontWeight: "bold",
          fontSize: isMobile ? "12px" : "14px"
        }}
      >
        {textConfig.PROFILE_FORM_STEPS_AVAILABLE_COLLECTIONS}:
      </Typography>
      
      <Box 
        sx={{ 
          maxHeight: "280px", 
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: isDarkMode ? "#18181B" : "#F9FAFB",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDarkMode ? "#3F3F46" : "#D1D5DB",
            borderRadius: "3px",
          },
        }}
      >
        {availableCollections && availableCollections.length > 0 ? (
          availableCollections.map((collection: string) => (
            <CollectionItem key={collection} $isDark={isDarkMode}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={profileData.collections.includes(collection)}
                    onChange={() => handleCollectionToggle(collection)}
                    size="small"
                    sx={{
                      color: isDarkMode ? "#71717A" : "#9CA3AF",
                      "&.Mui-checked": {
                        color: "#22C55E",
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ fontSize: isMobile ? "12px" : "14px" }}>
                      {collection}
                    </Typography>
                    {isDefaultCollection(collection) && (
                      <DefaultCollectionTag $isDark={isDarkMode}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Lock size={12} />
                          {textConfig.PROFILE_FORM_STEPS_DEFAULT_COLLECTION}
                        </Box>
                      </DefaultCollectionTag>
                    )}
                  </Box>
                }
              />
            </CollectionItem>
          ))
        ) : (
          <Box 
            sx={{ 
              textAlign: "center", 
              color: isDarkMode ? "#A1A1AA" : "#6B7280",
              py: 4,
              fontSize: isMobile ? "12px" : "14px"
            }}
          >
            {textConfig.PROFILE_FORM_STEPS_NO_COLLECTIONS}
          </Box>
        )}
      </Box>
    </FormContainer>
  );

  const renderStep2 = () => (
    <FormContainer $isDark={isDarkMode}>
      <InfoBox $isDark={isDarkMode}>
        <Info className="info-icon" size={isMobile ? 28 : 34} />
        <Typography className="info-text" sx={{ fontSize: isMobile ? "12px" : "13px" }}>
          {textConfig.PROFILE_FORM_STEPS_PROMPTS_INFO}
        </Typography>
      </InfoBox>
      
      <CollapseCard $isDark={isDarkMode}>
        <CollapseHeader 
          $isDark={isDarkMode}
          onClick={() => setAdjustmentsExpanded(!adjustmentsExpanded)}
        >
          <Typography sx={{ fontSize: isMobile ? "12px" : "14px" }}>
            {textConfig.PROFILE_FORM_STEPS_ADDITIONAL_SETTINGS}
          </Typography>
          {adjustmentsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </CollapseHeader>
        <Collapse in={adjustmentsExpanded}>
          <CollapseContent $isDark={isDarkMode}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={profileData.includeHistory}
                  onChange={(e) => setProfileData(prev => ({ ...prev, includeHistory: e.target.checked }))}
                  size="small"
                  sx={{
                    color: isDarkMode ? "#71717A" : "#9CA3AF",
                    "&.Mui-checked": {
                      color: "#22C55E",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  color: isDarkMode ? "#FFFFFF" : "#1F2937", 
                }}>
                  <Typography sx={{ fontSize: isMobile ? "12px" : "14px" }}>
                    {textConfig.PROFILE_FORM_STEPS_HISTORY_LABEL}
                  </Typography>
                  <Box 
                    sx={{
                      ml: 1,
                      display: "inline-block",
                      padding: "4px 8px",
                      backgroundColor: "#22C55E",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  >
                    {profileData.includeHistory ? textConfig.PROFILE_FORM_STEPS_ACTIVATED : textConfig.PROFILE_FORM_STEPS_DEACTIVATED}
                  </Box>
                </Box>
              }
            />
            <Typography 
              sx={{ 
                fontSize: "12px", 
                color: isDarkMode ? "#A1A1AA" : "#6B7280",
                mt: 1 
              }}
            >
              {textConfig.PROFILE_FORM_STEPS_HISTORY_DESCRIPTION}
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ 
                mb: 2,
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "bold",
                color: isDarkMode ? "#FFFFFF" : "#1F2937",
              }}>
                {textConfig.PROFILE_FORM_STEPS_MODEL_ID_LABEL || "Modelo de IA"}
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <Box
                  onClick={() => setModelIdOpen(!modelIdOpen)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 12px",
                    fontSize: "12px",
                    border: `1px solid ${isDarkMode ? "#374151" : "#D1D5DB"}`,
                    borderRadius: "6px",
                    backgroundColor: isDarkMode ? "#27272A" : "#FFFFFF",
                    color: isDarkMode ? "#FFFFFF" : "#1F2937",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <span>
                    {profileData.modelId === "" && "Elegir el modelo"}
                    {profileData.modelId === "anthropic.claude-3-haiku-20240307-v1:0" && "Claude 3 Haiku - anthropic.claude-3-haiku-20240307-v1:0"}
                    {profileData.modelId === "us.anthropic.claude-3-5-haiku-20241022-v1:0" && "Claude 3.5 Haiku - us.anthropic.claude-3-5-haiku-20241022-v1:0"}
                    {profileData.modelId === "us.anthropic.claude-3-sonnet-20240229-v1:0" && "Claude 3 Sonnet - us.anthropic.claude-3-sonnet-20240229-v1:0"}
                  </span>
                  <ChevronDown 
                    size={16} 
                    style={{ 
                      transform: modelIdOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      color: isDarkMode ? "#FFFFFF" : "#1F2937",
                    }} 
                  />
                </Box>
                {modelIdOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: isDarkMode ? "#27272A" : "#FFFFFF",
                      border: `1px solid ${isDarkMode ? "#374151" : "#D1D5DB"}`,
                      borderRadius: "6px",
                      marginTop: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      fontSize: "12px",
                    }}
                  >
                                         
                    
                                         {profileData.modelId !== "anthropic.claude-3-haiku-20240307-v1:0" && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, modelId: "anthropic.claude-3-haiku-20240307-v1:0" }));
                           setModelIdOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         Claude 3 Haiku - anthropic.claude-3-haiku-20240307-v1:0
                       </Box>
                     )}
                     {profileData.modelId !== "us.anthropic.claude-3-5-haiku-20241022-v1:0" && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, modelId: "us.anthropic.claude-3-5-haiku-20241022-v1:0" }));
                           setModelIdOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         Claude 3.5 Haiku - us.anthropic.claude-3-5-haiku-20241022-v1:0
                       </Box>
                     )}
                     {profileData.modelId !== "us.anthropic.claude-3-sonnet-20240229-v1:0" && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, modelId: "us.anthropic.claude-3-sonnet-20240229-v1:0" }));
                           setModelIdOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         Claude 3 Sonnet - us.anthropic.claude-3-sonnet-20240229-v1:0
                       </Box>
                     )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ 
                mb: 2, 
                fontSize: isMobile ? "12px" : "14px", 
                fontWeight: "bold", 
                color: isDarkMode ? "#FFFFFF" : "#1F2937",
              }}>
                {textConfig.PROFILE_FORM_STEPS_TEMPERATURE_LABEL || "Temperatura"}
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <Box
                  onClick={() => setTemperatureOpen(!temperatureOpen)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 12px",
                    fontSize: "12px",
                    border: `1px solid ${isDarkMode ? "#374151" : "#D1D5DB"}`,
                    borderRadius: "6px",
                    backgroundColor: isDarkMode ? "#27272A" : "#FFFFFF",
                    color: isDarkMode ? "#FFFFFF" : "#1F2937",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <span>
                    {profileData.temperature === null && "Elegir la temperatura"}
                    {profileData.temperature === 0 && "0"}
                    {profileData.temperature === 0.1 && "0.1"}
                    {profileData.temperature === 0.2 && "0.2"}
                    {profileData.temperature === 0.3 && "0.3"}
                    {profileData.temperature === 0.4 && "0.4"}
                    {profileData.temperature === 0.5 && "0.5"}
                    {profileData.temperature === 0.6 && "0.6"}
                    {profileData.temperature === 0.7 && "0.7"}
                    {profileData.temperature === 0.8 && "0.8"}
                    {profileData.temperature === 0.9 && "0.9"}
                    {profileData.temperature === 1 && "1"}
                  </span>
                  <ChevronDown 
                    size={16} 
                    style={{ 
                      transform: temperatureOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      color: isDarkMode ? "#FFFFFF" : "#1F2937",
                    }} 
                  />
                </Box>
                {temperatureOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: isDarkMode ? "#27272A" : "#FFFFFF",
                      border: `1px solid ${isDarkMode ? "#374151" : "#D1D5DB"}`,
                      borderRadius: "6px",
                      marginTop: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      fontSize: "12px",
                    }}
                  >
                                         
                                                             
                                         {profileData.temperature !== 0 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0
                       </Box>
                     )}
                     {profileData.temperature !== 0.1 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.1 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.1
                       </Box>
                     )}
                     {profileData.temperature !== 0.2 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.2 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.2
                       </Box>
                     )}
                     {profileData.temperature !== 0.3 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.3 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.3
                       </Box>
                     )}
                     {profileData.temperature !== 0.4 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.4 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.4
                       </Box>
                     )}
                     {profileData.temperature !== 0.5 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.5 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.5
                       </Box>
                     )}
                     {profileData.temperature !== 0.6 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.6 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.6
                       </Box>
                     )}
                     {profileData.temperature !== 0.7 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.7 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.7
                       </Box>
                     )}
                     {profileData.temperature !== 0.8 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.8 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.8
                       </Box>
                     )}
                     {profileData.temperature !== 0.9 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 0.9 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         0.9
                       </Box>
                     )}
                     {profileData.temperature !== 1 && (
                       <Box
                         onClick={() => {
                           setProfileData(prev => ({ ...prev, temperature: 1 }));
                           setTemperatureOpen(false);
                         }}
                         sx={{
                           padding: "8px 12px",
                           cursor: "pointer",
                           "&:hover": {
                             backgroundColor: isDarkMode ? "#3F3F46" : "#F3F4F6",
                           },
                         }}
                       >
                         1
                       </Box>
                     )}
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontSize: "11px", color: isDarkMode ? "#A1A1AA" : "#6B7280", mt: 1 }}>
                Debe ingresar entre el número 0 al número 1 máximo
              </Typography>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ 
                mb: 2, 
                fontSize: isMobile ? "12px" : "14px", 
                fontWeight: "bold", 
                color: profileData.numSearchDocuments !== null && (profileData.numSearchDocuments > 20 || profileData.numSearchDocuments < 1) ? "#EF4444" : "#22C55E"
              }}>
                {textConfig.PROFILE_FORM_STEPS_NUM_SEARCH_DOCS_LABEL || "Número de documentos de búsqueda"}
              </Typography>
              <Box sx={{ position: "relative" }}>
                <input
                  type="text"
                  value={profileData.numSearchDocuments !== null ? profileData.numSearchDocuments : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) {
                      const numValue = value === "" ? null : parseInt(value);
                      setProfileData(prev => ({ ...prev, numSearchDocuments: numValue }));
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setProfileData(prev => ({ ...prev, numSearchDocuments: null }));
                      return;
                    }
                    const numValue = parseInt(value);
                    if (isNaN(numValue)) {
                      setProfileData(prev => ({ ...prev, numSearchDocuments: null }));
                      return;
                    }
                    let finalValue = numValue;
                    if (numValue < 1) {
                      finalValue = 1;
                    } else if (numValue > 20) {
                      finalValue = 20;
                    }
                    setProfileData(prev => ({ ...prev, numSearchDocuments: finalValue }));
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    paddingRight: "40px",
                    fontSize: "14px",
                    border: `1px solid ${profileData.numSearchDocuments !== null && (profileData.numSearchDocuments > 20 || profileData.numSearchDocuments < 1) ? "#EF4444" : "#22C55E"}`,
                    borderRadius: "6px",
                    backgroundColor: isDarkMode ? "#27272A" : "#FFFFFF",
                    color: isDarkMode ? "#FFFFFF" : "#1F2937",
                    outline: "none",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {profileData.numSearchDocuments !== null && (profileData.numSearchDocuments > 20 || profileData.numSearchDocuments < 1) ? 
                    <X size={16} color="#EF4444" /> : 
                    <Check size={16} color="#22C55E" />
                  }
                </Box>
              </Box>
              <Typography sx={{ 
                fontSize: "11px", 
                color: isDarkMode ? "#A1A1AA" : "#6B7280", 
                mt: 1 
              }}>
                Debe ingresar entre el número 1 al número 20 máximo
              </Typography>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ 
                mb: 2, 
                fontSize: isMobile ? "12px" : "14px", 
                fontWeight: "bold", 
                color: profileData.numChatMessages !== null && (profileData.numChatMessages < 2 || profileData.numChatMessages > 100 || profileData.numChatMessages % 2 !== 0) ? "#EF4444" : "#22C55E"
              }}>
                {textConfig.PROFILE_FORM_STEPS_NUM_CHAT_MSGS_LABEL || "Número de mensajes de chat"}
              </Typography>
              <Box sx={{ position: "relative" }}>
                <input
                  type="text"
                  value={profileData.numChatMessages !== null ? profileData.numChatMessages : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) {
                      const numValue = value === "" ? null : parseInt(value);
                      setProfileData(prev => ({ ...prev, numChatMessages: numValue }));
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setProfileData(prev => ({ ...prev, numChatMessages: null }));
                      return;
                    }
                    const numValue = parseInt(value);
                    if (isNaN(numValue)) {
                      setProfileData(prev => ({ ...prev, numChatMessages: null }));
                      return;
                    }
                    let finalValue = numValue;
                    if (numValue < 2) {
                      finalValue = 2;
                    } else if (numValue > 100) {
                      finalValue = 100;
                    } else if (numValue % 2 !== 0) {
                      finalValue = Math.round(numValue / 2) * 2;
                      if (finalValue < 2) finalValue = 2;
                      if (finalValue > 100) finalValue = 100;
                    }
                    setProfileData(prev => ({ ...prev, numChatMessages: finalValue }));
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    paddingRight: "40px",
                    fontSize: "14px",
                    border: `1px solid ${profileData.numChatMessages !== null && (profileData.numChatMessages < 2 || profileData.numChatMessages > 100 || profileData.numChatMessages % 2 !== 0) ? "#EF4444" : "#22C55E"}`,
                    borderRadius: "6px",
                    backgroundColor: isDarkMode ? "#27272A" : "#FFFFFF",
                    color: isDarkMode ? "#FFFFFF" : "#1F2937",
                    outline: "none",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {profileData.numChatMessages !== null && (profileData.numChatMessages < 2 || profileData.numChatMessages > 100 || profileData.numChatMessages % 2 !== 0) ? 
                    <X size={16} color="#EF4444" /> : 
                    <Check size={16} color="#22C55E" />
                  }
                </Box>
              </Box>
              <Typography sx={{ fontSize: "11px", color: isDarkMode ? "#A1A1AA" : "#6B7280", mt: 1 }}>
                Debe ingresar entre el número 2 al número 100 máximo (siempre números pares)
              </Typography>
            </Box>
          </CollapseContent>
        </Collapse>
      </CollapseCard>

      <CollapseCard $isDark={isDarkMode}>
        <CollapseHeader 
          $isDark={isDarkMode}
          onClick={() => setInstructionsExpanded(!instructionsExpanded)}
        >
          <Typography sx={{ fontSize: isMobile ? "12px" : "14px" }}>
            {textConfig.PROFILE_FORM_STEPS_SYSTEM_INSTRUCTIONS}
          </Typography>
          {instructionsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </CollapseHeader>
        <Collapse in={instructionsExpanded}>
          <CollapseContent $isDark={isDarkMode}>
            <StyledTextarea
              placeholder={textConfig.PROFILE_FORM_STEPS_INSTRUCTIONS_PLACEHOLDER}
              value={profileData.systemInstruction}
              onChange={(e) => setProfileData(prev => ({ ...prev, systemInstruction: e.target.value }))}
              $isDark={isDarkMode}
            />
            <Box sx={{ 
              display: "flex", 
              justifyContent: "flex-end",
              mt: 1,
              fontSize: "12px",
              color: isDarkMode ? "#A1A1AA" : "#6B7280",
            }}>
              {textConfig.PROFILE_FORM_STEPS_CHARACTER_COUNT?.replace('{current}', profileData.systemInstruction.length.toString()).replace('{max}', '5000')}
            </Box>
          </CollapseContent>
        </Collapse>
      </CollapseCard>
    </FormContainer>
  );

  if (showPreview) {
    return renderPreview();
  }

  switch (activeStep) {
    case 0:
      return renderStep0();
    case 1:
      return renderStep1();
    case 2:
      return renderStep2();
    default:
      return null;
  }
};

export default ProfileFormSteps;